import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
  LinearProgress,
  Alert,
  Switch,
  FormGroup,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack
} from '@mui/material';
import { Pill, Plus, CheckCircle, Calendar, TrendingUp, Edit, Save, X, Trash2, Settings, PlusCircle, GripVertical } from 'lucide-react';
import { SupplementEntry, DatabaseSchema, SupplementDefinition } from '../types';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { WeekNavigation } from './WeekNavigation';
import { getWeekDays } from '../utils/weekUtils';
import { useToast } from './shared/ToastProvider';

interface SupplementTrackerProps {
  database: DatabaseSchema | null;
  onAddSupplement: (supplement: SupplementEntry) => Promise<void>;
  onDeleteSupplement?: (id: string) => Promise<void>;
  onUpdatePreferences?: (preferences: any) => Promise<void>;
}

const defaultSupplements: SupplementDefinition[] = [
  { id: 'multivitamin', name: 'Multivitamin', color: '#f59e0b', enabled: true, order: 0 },
  { id: 'vitaminD3', name: 'Vitamin D3', color: '#eab308', enabled: true, order: 1 },
  { id: 'omega3', name: 'Omega-3', color: '#6366f1', enabled: true, order: 2 },
  { id: 'orlistatLunch', name: 'Orlistat (Lunch)', color: '#22c55e', enabled: true, order: 3 },
  { id: 'orlistatDinner', name: 'Orlistat (Dinner)', color: '#10b981', enabled: true, order: 4 },
  { id: 'methylphenidate', name: 'Methylphenidate 36mg XR', color: '#ef4444', enabled: true, order: 5 },
];

export const SupplementTracker: React.FC<SupplementTrackerProps> = ({ 
  database, 
  onAddSupplement, 
  onDeleteSupplement,
  onUpdatePreferences 
}) => {
  const toast = useToast();
  const [supplements, setSupplements] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [editSupplements, setEditSupplements] = useState<{ [key: string]: boolean }>({});
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [supplementDefinitions, setSupplementDefinitions] = useState<SupplementDefinition[]>(defaultSupplements);
  const [newSupplementName, setNewSupplementName] = useState('');
  const [newSupplementColor, setNewSupplementColor] = useState('#6366f1');
  const [editingSupplementId, setEditingSupplementId] = useState<string | null>(null);
  const [editSupplementName, setEditSupplementName] = useState('');
  const [editSupplementColor, setEditSupplementColor] = useState('');
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);

  const supplementEntries = database?.supplements || [];
  
  // Load custom supplements from preferences
  useEffect(() => {
    if (database?.preferences?.customSupplements) {
      // Sort by order if it exists, otherwise maintain existing order
      const sorted = [...database.preferences.customSupplements].sort((a, b) => {
        const orderA = a.order ?? 999;
        const orderB = b.order ?? 999;
        return orderA - orderB;
      });
      setSupplementDefinitions(sorted);
    }
  }, [database?.preferences?.customSupplements]);

  // Initialize supplements state based on definitions
  useEffect(() => {
    const initialSupplements: { [key: string]: boolean } = {};
    supplementDefinitions.filter(s => s.enabled).forEach(supp => {
      initialSupplements[supp.id] = false;
    });
    setSupplements(prev => {
      // Preserve existing values for enabled supplements
      const newState: { [key: string]: boolean } = {};
      supplementDefinitions.filter(s => s.enabled).forEach(supp => {
        newState[supp.id] = prev[supp.id] || false;
      });
      return newState;
    });
  }, [supplementDefinitions]);
  
  // Get weekly supplement data
  const getWeeklySupplements = () => {
    const weekDays = getWeekDays(selectedWeek);
    
    return weekDays.map(day => {
      const dayEntry = supplementEntries.find(entry => entry.date === day.dateString);
      const daySupplements: { [key: string]: boolean } = {};
      
      // Initialize with current supplement definitions
      supplementDefinitions.filter(s => s.enabled).forEach(supp => {
        daySupplements[supp.id] = dayEntry?.supplements?.[supp.id] || false;
      });
      
      return {
        ...day,
        supplements: daySupplements,
        hasEntry: !!dayEntry
      };
    });
  };

  const weeklySupplements = getWeeklySupplements();

  // Handle editing a specific day
  const handleEditDay = (dateString: string) => {
    const dayData = weeklySupplements.find(d => d.dateString === dateString);
    if (dayData) {
      setEditSupplements(dayData.supplements);
      setEditingDay(dateString);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingDay) return;
    
    setQuickAddLoading(true);
    try {
      const dateObj = new Date(editingDay);
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date:', editingDay);
        return;
      }

      const existingEntry = supplementEntries.find(e => e.date === editingDay);
      const newEntry: SupplementEntry = {
        id: existingEntry?.id || `supplement_${Date.now()}`,
        date: editingDay,
        supplements: editSupplements,
      };

      await onAddSupplement(newEntry);
      setEditingDay(null);
      toast.success('Supplements saved successfully');
    } catch (error) {
      console.error('Error saving supplement entry:', error);
      toast.error('Failed to save supplements');
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleEditSupplementChange = (supplementId: string) => {
    setEditSupplements(prev => ({
      ...prev,
      [supplementId]: !prev[supplementId]
    }));
  };

  const handleDeleteSupplement = async (dateString: string) => {
    if (!onDeleteSupplement) return;
    
    const dayData = weeklySupplements.find(d => d.dateString === dateString);
    if (!dayData?.hasEntry) return;
    
    const supplementEntry = supplementEntries.find(s => s.date === dateString);
    if (!supplementEntry) return;
    
    const confirmDelete = window.confirm(`Delete supplement entry from ${format(new Date(dateString), 'MMM dd, yyyy')}?`);
    if (confirmDelete) {
      await onDeleteSupplement(supplementEntry.id);
    }
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => {
    return (
      <div hidden={value !== index}>
        {value === index && <Box>{children}</Box>}
      </div>
    );
  };
  
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const todayEntry = supplementEntries.find(s => s.date === todayStr);

  // Initialize today's supplements if they exist
  React.useEffect(() => {
    if (todayEntry) {
      const todaySupps: { [key: string]: boolean } = {};
      supplementDefinitions.filter(s => s.enabled).forEach(supp => {
        todaySupps[supp.id] = todayEntry.supplements[supp.id] || false;
      });
      setSupplements(todaySupps);
    }
  }, [todayEntry, supplementDefinitions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const newEntry: SupplementEntry = {
        id: todayEntry?.id || `supplement_${Date.now()}`,
        date: todayStr,
        supplements,
      };

      await onAddSupplement(newEntry);
      toast.success('Supplements saved successfully');
    } catch (error) {
      console.error('Error adding supplement:', error);
      toast.error('Failed to save supplements');
    } finally {
      setLoading(false);
    }
  };

  const handleSupplementChange = (supplementId: string) => {
    setSupplements(prev => ({
      ...prev,
      [supplementId]: !prev[supplementId]
    }));
  };

  const handleAddSupplement = async () => {
    if (!newSupplementName.trim()) return;
    
    const maxOrder = Math.max(...supplementDefinitions.map(s => s.order ?? 0), -1);
    const newSupplement: SupplementDefinition = {
      id: `custom_${Date.now()}`,
      name: newSupplementName,
      color: newSupplementColor,
      enabled: true,
      order: maxOrder + 1
    };
    
    const updatedDefinitions = [...supplementDefinitions, newSupplement];
    setSupplementDefinitions(updatedDefinitions);
    
    // Save to preferences
    if (onUpdatePreferences && database?.preferences) {
      await onUpdatePreferences({
        ...database.preferences,
        customSupplements: updatedDefinitions
      });
    }
    
    setNewSupplementName('');
    setNewSupplementColor('#6366f1');
  };

  const handleUpdateSupplement = async (id: string) => {
    if (!editSupplementName.trim()) return;
    
    const updatedDefinitions = supplementDefinitions.map(s => 
      s.id === id 
        ? { ...s, name: editSupplementName, color: editSupplementColor } 
        : s
    );
    
    setSupplementDefinitions(updatedDefinitions);
    
    // Save to preferences
    if (onUpdatePreferences && database?.preferences) {
      await onUpdatePreferences({
        ...database.preferences,
        customSupplements: updatedDefinitions
      });
    }
    
    setEditingSupplementId(null);
    setEditSupplementName('');
    setEditSupplementColor('');
  };

  const handleToggleSupplementEnabled = async (id: string) => {
    const updatedDefinitions = supplementDefinitions.map(s => 
      s.id === id ? { ...s, enabled: !s.enabled } : s
    );
    
    setSupplementDefinitions(updatedDefinitions);
    
    // Save to preferences
    if (onUpdatePreferences && database?.preferences) {
      await onUpdatePreferences({
        ...database.preferences,
        customSupplements: updatedDefinitions
      });
    }
  };

  const handleDeleteSupplementDefinition = async (id: string) => {
    // Don't allow deleting default supplements
    if (['multivitamin', 'vitaminD3', 'omega3', 'orlistatLunch', 'orlistatDinner'].includes(id)) {
      alert('Cannot delete default supplements. You can disable them instead.');
      return;
    }
    
    const confirmDelete = window.confirm('Delete this supplement? This will remove it from all entries.');
    if (!confirmDelete) return;
    
    const updatedDefinitions = supplementDefinitions.filter(s => s.id !== id);
    setSupplementDefinitions(updatedDefinitions);
    
    // Save to preferences
    if (onUpdatePreferences && database?.preferences) {
      await onUpdatePreferences({
        ...database.preferences,
        customSupplements: updatedDefinitions
      });
    }
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedItem(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDragEnter = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    setDragOverItem(id);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverItem(null);
  };

  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragOverItem(null);
    
    if (!draggedItem || draggedItem === targetId) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = supplementDefinitions.findIndex(s => s.id === draggedItem);
    const targetIndex = supplementDefinitions.findIndex(s => s.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      return;
    }

    // Create a new array with updated order
    const newDefinitions = [...supplementDefinitions];
    const [removed] = newDefinitions.splice(draggedIndex, 1);
    newDefinitions.splice(targetIndex, 0, removed);

    // Update order values
    const updatedDefinitions = newDefinitions.map((supp, index) => ({
      ...supp,
      order: index
    }));

    setSupplementDefinitions(updatedDefinitions);
    setDraggedItem(null);

    // Save to preferences
    if (onUpdatePreferences && database?.preferences) {
      await onUpdatePreferences({
        ...database.preferences,
        customSupplements: updatedDefinitions
      });
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const getWeeklyStats = () => {
    const weekStart = startOfWeek(new Date());
    const weekEnd = endOfWeek(new Date());
    
    const weekEntries = supplementEntries.filter(entry => {
      const entryDate = new Date(entry.date);
      return entryDate >= weekStart && entryDate <= weekEnd;
    });

    const stats: { [key: string]: { taken: number, total: number } } = {};
    
    supplementDefinitions.filter(s => s.enabled).forEach(supp => {
      stats[supp.id] = { taken: 0, total: 7 };
    });

    weekEntries.forEach(entry => {
      Object.keys(entry.supplements).forEach(key => {
        if (entry.supplements[key] && stats[key]) {
          stats[key].taken++;
        }
      });
    });

    return stats;
  };

  const getRecentEntries = () => {
    return [...supplementEntries]
      .filter(entry => entry.date !== todayStr)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 7);
  };

  const getTodayCompletion = () => {
    const enabledSupplements = supplementDefinitions.filter(s => s.enabled);
    const total = enabledSupplements.length;
    const completed = enabledSupplements.filter(s => supplements[s.id]).length;
    return { completed, total, percentage: total > 0 ? (completed / total) * 100 : 0 };
  };

  const weeklyStats = getWeeklyStats();
  const recentEntries = getRecentEntries();
  const todayCompletion = getTodayCompletion();
  const enabledSupplements = supplementDefinitions.filter(s => s.enabled);

  return (
    <Box p={{ xs: 1, sm: 2, md: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Supplement Tracking
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Settings size={20} />}
          onClick={() => setSettingsOpen(true)}
        >
          Manage Supplements
        </Button>
      </Box>

      <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Daily Entry" />
        <Tab label="Weekly View" />
      </Tabs>

      <TabPanel value={currentTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Plus size={24} color="#6366f1" />
                <Typography variant="h6">Today's Supplements</Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <FormGroup>
                  {enabledSupplements.map((supp) => (
                    <FormControlLabel
                      key={supp.id}
                      control={
                        <Switch
                          checked={supplements[supp.id] || false}
                          onChange={() => handleSupplementChange(supp.id)}
                          color="primary"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Pill size={16} color={supp.color} />
                          <Typography>{supp.name}</Typography>
                        </Box>
                      }
                      sx={{ mb: 1 }}
                    />
                  ))}
                </FormGroup>
                
                {enabledSupplements.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No supplements enabled. Click "Manage Supplements" to add or enable supplements.
                  </Alert>
                )}
                
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading || enabledSupplements.length === 0}
                  sx={{ py: 1.5, mt: 3 }}
                >
                  {loading ? 'Saving...' : 'Save Supplements'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Today's Progress
              </Typography>
              
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    Supplements taken
                  </Typography>
                  <Typography variant="body2">
                    {todayCompletion.completed}/{todayCompletion.total}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={todayCompletion.percentage} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {todayCompletion.percentage === 100 && todayCompletion.total > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircle size={16} />
                    Great job! All supplements taken today.
                  </Box>
                </Alert>
              )}

              <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
                {enabledSupplements.map((supp) => (
                  <Chip
                    key={supp.id}
                    size="small"
                    label={supp.name}
                    color={supplements[supp.id] ? "success" : "default"}
                    variant={supplements[supp.id] ? "filled" : "outlined"}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <TrendingUp size={24} color="#10b981" />
                <Typography variant="h6">Weekly Adherence</Typography>
              </Box>
              
              {Object.entries(weeklyStats).map(([key, stats]) => {
                const supp = supplementDefinitions.find(s => s.id === key);
                if (!supp) return null;
                
                return (
                  <Box key={key} mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Pill size={14} color={supp.color} />
                        <Typography variant="body2">
                          {supp.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {stats.taken}/{stats.total} days
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(stats.taken / stats.total) * 100}
                      sx={{ 
                        height: 6, 
                        borderRadius: 3,
                        backgroundColor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: supp.color
                        }
                      }}
                    />
                  </Box>
                );
              })}
            </CardContent>
          </Card>

          {recentEntries.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Calendar size={24} color="#6366f1" />
                  <Typography variant="h6">Recent History</Typography>
                </Box>
                
                <List>
                  {recentEntries.map((entry, index) => {
                    const entrySupplementCount = enabledSupplements.filter(s => entry.supplements[s.id]).length;
                    const total = enabledSupplements.length;
                    const percentage = total > 0 ? (entrySupplementCount / total) * 100 : 0;
                    
                    return (
                      <React.Fragment key={entry.id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                                <Typography variant="body1">
                                  {format(new Date(entry.date), 'MMM dd, yyyy')}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={`${entrySupplementCount}/${total}`}
                                  color={percentage === 100 ? "success" : percentage >= 60 ? "warning" : "error"}
                                />
                              </Box>
                            }
                            secondary={
                              <Box mt={1}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={percentage}
                                  sx={{ height: 4, borderRadius: 2 }}
                                />
                                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                                  {enabledSupplements.map((supp) => (
                                    entry.supplements[supp.id] && (
                                      <Chip
                                        key={supp.id}
                                        size="small"
                                        label={supp.name}
                                        sx={{ 
                                          backgroundColor: supp.color,
                                          color: 'white',
                                          fontSize: '0.7rem'
                                        }}
                                      />
                                    )
                                  ))}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < recentEntries.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <Box mb={3}>
          <WeekNavigation
            currentWeek={selectedWeek}
            onWeekChange={setSelectedWeek}
          />
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Weekly Supplement Overview
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    {enabledSupplements.map(supp => (
                      <TableCell key={supp.id} align="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Pill size={12} color={supp.color} />
                          <Typography variant="caption">{supp.name}</Typography>
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell>Progress</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklySupplements.map((day) => {
                    const completed = enabledSupplements.filter(s => day.supplements[s.id]).length;
                    const total = enabledSupplements.length;
                    const percentage = total > 0 ? (completed / total) * 100 : 0;
                    
                    return (
                      <TableRow 
                        key={day.dateString}
                        sx={{
                          backgroundColor: day.isToday ? 'rgba(99, 102, 241, 0.08)' : 'inherit'
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={day.isToday ? 'bold' : 'normal'}>
                              {day.dayShort}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(day.date, 'MM/dd')}
                            </Typography>
                          </Box>
                        </TableCell>
                        {enabledSupplements.map(supp => (
                          <TableCell key={supp.id} align="center">
                            <Checkbox
                              checked={day.supplements[supp.id] || false}
                              disabled
                              size="small"
                            />
                          </TableCell>
                        ))}
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress 
                              variant="determinate" 
                              value={percentage}
                              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption">
                              {completed}/{total}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" justifyContent="center" gap={0.5}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditDay(day.dateString)}
                              color="primary"
                            >
                              <Edit size={16} />
                            </IconButton>
                            {day.hasEntry && onDeleteSupplement && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteSupplement(day.dateString)}
                                color="error"
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Edit Day Dialog */}
      <Dialog open={!!editingDay} onClose={() => setEditingDay(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDay && (
            `Edit Supplements - ${format(new Date(editingDay), 'EEEE, MMM dd')}`
          )}
        </DialogTitle>
        <DialogContent>
          <FormGroup sx={{ mt: 2 }}>
            {enabledSupplements.map((supp) => (
              <FormControlLabel
                key={supp.id}
                control={
                  <Switch
                    checked={editSupplements[supp.id] || false}
                    onChange={() => handleEditSupplementChange(supp.id)}
                    color="primary"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Pill size={16} color={supp.color} />
                    <Typography>{supp.name}</Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingDay(null)}>
            <X size={16} style={{ marginRight: 4 }} />
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={quickAddLoading}
          >
            <Save size={16} style={{ marginRight: 4 }} />
            {quickAddLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplement Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Manage Supplements
        </DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              Add New Supplement
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Supplement Name"
                value={newSupplementName}
                onChange={(e) => setNewSupplementName(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Color"
                type="color"
                value={newSupplementColor}
                onChange={(e) => setNewSupplementColor(e.target.value)}
                size="small"
                sx={{ width: 100 }}
              />
              <Button
                variant="contained"
                startIcon={<PlusCircle size={16} />}
                onClick={handleAddSupplement}
                disabled={!newSupplementName.trim()}
              >
                Add
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">
              Current Supplements
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Drag to reorder
            </Typography>
          </Box>
          <List>
            {supplementDefinitions.map((supp) => 
              editingSupplementId === supp.id ? (
                // Edit mode - use a custom layout without ListItem
                <Box
                  key={supp.id}
                  sx={{
                    p: 2,
                    border: '1px solid #e2e8f0',
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: '#f8fafc'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      value={editSupplementName}
                      onChange={(e) => setEditSupplementName(e.target.value)}
                      size="small"
                      placeholder="Supplement name"
                      sx={{ flex: 1 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Color:
                      </Typography>
                      <input
                        type="color"
                        value={editSupplementColor}
                        onChange={(e) => setEditSupplementColor(e.target.value)}
                        style={{ 
                          width: 36, 
                          height: 36, 
                          border: '1px solid #ccc',
                          borderRadius: 4,
                          cursor: 'pointer',
                          padding: 0
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateSupplement(supp.id)}
                        color="primary"
                        sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': { backgroundColor: 'primary.dark' }
                        }}
                      >
                        <Save size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingSupplementId(null);
                          setEditSupplementName('');
                          setEditSupplementColor('');
                        }}
                        sx={{ 
                          backgroundColor: 'grey.300',
                          '&:hover': { backgroundColor: 'grey.400' }
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </Box>
                  </Stack>
                </Box>
              ) : (
                // Normal mode - use standard ListItem
                <ListItem
                  key={supp.id}
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, supp.id)}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, supp.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, supp.id)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    cursor: 'move',
                    opacity: draggedItem === supp.id ? 0.5 : 1,
                    transition: 'all 0.2s',
                    borderTop: dragOverItem === supp.id && draggedItem !== supp.id ? '2px solid #6366f1' : '2px solid transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  secondaryAction={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Switch
                        checked={supp.enabled}
                        onChange={() => handleToggleSupplementEnabled(supp.id)}
                        color="primary"
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingSupplementId(supp.id);
                          setEditSupplementName(supp.name);
                          setEditSupplementColor(supp.color);
                        }}
                      >
                        <Edit size={16} />
                      </IconButton>
                      {!['multivitamin', 'vitaminD3', 'omega3', 'orlistatLunch', 'orlistatDinner'].includes(supp.id) && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSupplementDefinition(supp.id)}
                          color="error"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      )}
                    </Box>
                  }
                >
                  <Box display="flex" alignItems="center" sx={{ flex: 1 }}>
                    <GripVertical 
                      size={20} 
                      color="#64748b"
                      style={{ marginRight: 8, cursor: 'grab' }}
                    />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Pill size={16} color={supp.color} />
                          <Typography>{supp.name}</Typography>
                          {!supp.enabled && (
                            <Chip label="Disabled" size="small" variant="outlined" />
                          )}
                        </Box>
                      }
                    />
                  </Box>
                </ListItem>
              )
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};